import { DialogTitle, useTheme } from '@mui/material';
import { Theme as AugmentedTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import React, { useEffect } from 'react';
import { useTranslation, TFunction } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  TableActionTypes,
  TableState,
  TABLE_INITIAL_STATE,
  Typography,
  useDataTable,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useParams } from 'react-router-dom';
import {
  AuditContent,
  useGetAdminAuditLog,
} from '../../../../api-refactored/non-national-exam-registration/useGetAdminAuditLog';
import { PaginatedData } from '../../../../models/List';
import { stringToLongDateAndShortTime } from '../../../../utils/DateUtils';

interface IViewAuditLog {
  isOpenDialog: boolean;
  onClose: () => void;
}

enum ItemStatus {
  STATUS = 'Status',
  DOCUMENT_ADD = 'Document Added',
  DOCUMENT_DELETE = 'Document Deleted',
  DATA_ADD = 'Data Added',
  DATA_DELETE = 'Data Deleted',
  DATA_UPDATE = 'Data Updated',
}

const renderChangeSummaryColumn = (currentRow: any, t: TFunction<'translation'>) => {
  let element;
  switch (currentRow.item) {
    case ItemStatus.STATUS:
    case ItemStatus.DOCUMENT_ADD:
    case ItemStatus.DOCUMENT_DELETE:
      element = <Typography variant="body1">{currentRow.changeSummary.after}</Typography>;
      break;

    case ItemStatus.DATA_ADD:
    case ItemStatus.DATA_DELETE:
    case ItemStatus.DATA_UPDATE:
      element = (
        <>
          {currentRow.changeSummary.fieldName ? (
            <Typography variant="subtitle2">
              {currentRow.changeSummary.baseFieldName} - {currentRow.changeSummary.fieldName}
            </Typography>
          ) : (
            <Typography variant="subtitle2">{currentRow.changeSummary.baseFieldName}</Typography>
          )}
          <Typography variant="body2">
            {t('admin->>AuditLog.OldValue')}: {currentRow.changeSummary.before || '-'}
          </Typography>
          <Typography variant="body2">
            {t('admin->>AuditLog.NewValue')}: {currentRow.changeSummary.after || '-'}
          </Typography>
        </>
      );
      break;

    default:
      break;
  }

  return element;
};

const useStyles = makeStyles()((theme) =>
({
  radioButton: {
    padding: '0px',
    margin: '0px',
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  buttonContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  addIcon: {
    verticalAlign: 'sub',
    marginRight: 10,
    color: theme.color.icon.iconInverse,
  },
  selectSubjectText: {
    marginBottom: theme.spacing(3),
  },
  reasonTitle: {
    marginBottom: theme.spacing(2),
  },
  reasonInstruction: {
    marginBottom: theme.spacing(3),
  },
  customSelect: {
    marginTop: theme.spacing(3),
  },
  divider: {
    marginTop: theme.spacing(5),
  },
  textDisabled: {
    color: theme.color.text.textDisabled,
  },
  icon: {
    color: theme.color.text.textTertiary,
    marginRight: theme.spacing(1),
  },
}));

const getTableColumns = (t: TFunction<'translation'>, state: TableState, classes: Record<string,string>) => {
  const tableColumns: MUIDataTableColumnDef[] = [
    {
      label: t('exam->>NonNationalExam.User'),
      name: 'user',
      options: {
        setCellProps: () => ({ style: { width: '18%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography variant="body1">{state.data[dataIndex].userName}</Typography>
            <Typography variant="subtitle2">{state.data[dataIndex].userRole}</Typography>
          </>
        ),
      },
    },
    {
      label: t('exam->>NonNationalExam.Item'),
      name: 'item',
    },
    {
      label: t('exam->>NonNationalExam.ChangeSummary'),
      name: 'changeSummary',
      options: {
        customBodyRenderLite: (dataIndex: number) =>
          renderChangeSummaryColumn(state.data[dataIndex], t),
      },
    },
    {
      label: t('exam->>NonNationalExam.DateAndTime'),
      name: 'dateTime',
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography variant="body2">
              {state.data[dataIndex].dateTime
                ? stringToLongDateAndShortTime(state.data[dataIndex].dateTime)
                : '-'}
            </Typography>
          </>
        ),
      },
    },
  ];
  return tableColumns;
};

const BootstrapDialogTitle = (props: any) => {
  const theme = useTheme();
  const { children, onClose, ...other } = props;

  return (
    (<DialogTitle id="customized-dialog-title" {...other}>
      <Grid container direction="row" alignItems="center" justify="space-between">
        <Grid>
          <Typography variant="h4">{children}</Typography>
        </Grid>
        <Grid>
          <IconButton
            name={IconName.cross}
            color={theme.color.icon.iconDefault}
            ariaLabel="mobile menu"
            onClick={onClose}
          // iconSize="L" 
          />
        </Grid>
      </Grid>
    </DialogTitle>)
  );
};

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

const ViewAuditLog = ({ isOpenDialog, onClose }: IViewAuditLog) => {
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const { classes } = useStyles();

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
  });

  const { isLoading, refetch } = useGetAdminAuditLog({
    uuid,
    page: state.page,
    size: state.size,
    onSuccess: (data: PaginatedData<AuditContent>) => {
      if (data) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: data.content,
            count: data.totalElements,
          },
        } as TableActionTypes);
      }
    },
  });

  useEffect(() => {
    if (isOpenDialog) {
      refetch();
    }
  }, [refetch, state.page, state.size, isOpenDialog]);

  const tableColumns = getTableColumns(t, state, classes);

  const customOptions: MUIDataTableOptions = {
    ...tOptions,
    pagination: true,
  };

  return (
    <Dialog maxWidth="md" open={isOpenDialog} onClose={onClose} scroll="body">
      {BootstrapDialogTitle({
        children: t('exam->>NonNationalExam.AuditLog'),
        onClose,
      })}
      <DialogContent>
        <MUIDataTable
          isLoading={isLoading}
          columns={tableColumns}
          data={state.data}
          state={state}
          dispatch={tableDispatch}
          options={customOptions}
          components={[]}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ViewAuditLog;
